<script setup lang="ts">
import { computed, onBeforeUnmount, onMounted, ref } from "vue";
import IDatePicker from "../../date-picker/src/date-picker.vue";
import ITimelineRuler from "../timeline-ruler/src/timeline-ruler.vue";
import { MonitorPlaybackProps } from "./monitor-playback";

defineOptions({
  name: "IMonitorPlayback",
});

// ==================== Props / State ====================
const props = defineProps(MonitorPlaybackProps);

// ==================== DOM Refs ====================
const playerContainerRef = ref<HTMLDivElement | null>(null);
const timelineRef = ref<InstanceType<typeof ITimelineRuler> | null>(null);

// ==================== UI State ====================
const kbpsInfo = ref("-- KB/s");
const overlayTimeText = ref("2026-05-02 15:02:17");
const errorMsg = ref("");

// ==================== Player State ====================
let jessibuca: any = null;
let isDestroying = false;
let retryCount = 0;
const MAX_RETRY = 3;
const currentUrl = ref(
  props.url ||
    "ws://cms.abaixun.com/websocket/0402b0580060f0df/0000000001.flv?starttime=20260502T150217&duration=3600&uuid=s1vwaoh0rse31rpgars33u1d68384buc",
);

// 日期时间选择器 - 从 currentUrl 的 starttime 解析初始值
const starttimeDefault = computed(() => {
  const m = currentUrl.value.match(/starttime=(\d{8}T\d{6})/i);
  if (m) {
    const raw = m[1]; // 20260502T150217
    const y = raw.slice(0, 4);
    const mo = raw.slice(4, 6);
    const d = raw.slice(6, 8);
    const h = raw.slice(9, 11);
    const mi = raw.slice(11, 13);
    const s = raw.slice(13, 15);
    return `${y}-${mo}-${d} ${h}:${mi}:${s}`; // 2026-05-02 15:02:17
  }
  return "";
});
const datetimeValue = ref(starttimeDefault.value);

// 日期时间变更 → 更新 URL 并重新播放
function onDatetimeChange(val: string) {
  if (!val) return;
  // "2026-05-02 15:02:17" → "20260502T150217"
  const ts = val.replace(/[- :]/g, "").replace(/(\d{8})(\d{6})/, "$1T$2");
  currentUrl.value = currentUrl.value.replace(
    /starttime=\d{8}T\d{6}/i,
    `starttime=${ts}`,
  );
  commitWithTime(Math.floor(new Date(val.replace(" ", "T")).getTime() / 1000));
}

// 记录播放起始时间，用于结合 timeUpdate 计算当前播放进度
let playStartTimeMs = 0;
let lastPtsMs = 0;

// Timer ref
let playTimerId: ReturnType<typeof setInterval> | null = null;

// ==================== Utils ====================
function pad(n: number) {
  return String(n).padStart(2, "0");
}

function syncStartTimeToUrl(unixSec: number) {
  const d = new Date(unixSec * 1000);
  const val = `${
    pad(d.getFullYear()) + pad(d.getMonth() + 1) + pad(d.getDate())
  }T${pad(d.getHours())}${pad(d.getMinutes())}${pad(d.getSeconds())}`;
  currentUrl.value = currentUrl.value.replace(
    /starttime=\d{8}T\d{6}/i,
    `starttime=${val}`,
  );
}

function fmtDatetime(s: number) {
  const d = new Date(s * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function commitWithTime(sec: number) {
  syncStartTimeToUrl(sec);
  datetimeValue.value = fmtDatetime(sec);
  playStream(currentUrl.value);
}

function fmtTF(s: number) {
  const d = new Date(s * 1000);
  return `${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`;
}

function fmtD(s: number) {
  const d = new Date(s * 1000);
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
}

// ==================== Player Functions ====================
function parseStartTime(url: string): Date | number | null {
  const m = url.match(/starttime=(\d{8}T\d{6})/i);
  if (m)
    return new Date(
      m[1].replace(
        /^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})/,
        "$1-$2-$3T$4:$5:$6",
      ),
    );
  const dm = url.match(/duration=(\d+)/i);
  if (dm) return parseInt(dm[1]);
  return null;
}

async function playStream(url: string) {
  if (!url) return;

  // 如果正在销毁，延迟等待后再执行
  if (isDestroying) {
    await new Promise<void>((r) => setTimeout(r, 600));
    return playStream(url);
  }

  await destroyPlayer();
  errorMsg.value = "";

  const st = parseStartTime(url);
  let dur = 3600;
  const dm = url.match(/duration=(\d+)/i);
  if (dm) dur = parseInt(dm[1]);

  // 记录播放起始时间
  if (st instanceof Date) {
    playStartTimeMs = st.getTime();
  }

  if (st && timelineRef.value) {
    timelineRef.value.initRuler(st as Date, dur);
  }

  // 创建 Jessibuca 实例
  createJessibuca(url);
}

function createJessibuca(url: string) {
  const win = window as any;
  if (!win.Jessibuca) {
    errorMsg.value = "Jessibuca 播放器未加载";
    return;
  }

  jessibuca = new win.Jessibuca({
    container: playerContainerRef.value!,
    decoder: "./plugins/jessibuca/decoder.js",
    videoBuffer: 0.2,
    isResize: false,
    isFullResize: false,
    hasAudio: true,
    debug: props.debug,
    supportDblclickFullscreen: true,
    showBandwidth: true,
    operateBtns: {
      fullscreen: true,
      screenshot: true,
      play: true,
      audio: true,
      record: true,
    },
    // 优先使用 WASM 软解，支持 H.265
    useMSE: false,
    useWCS: false,
    autoWasm: true,
    isNotMute: false,
    timeout: 10,
    heartTimeout: 10,
    loadingTimeout: 10,
    heartTimeoutReplay: true,
    heartTimeoutReplayTimes: MAX_RETRY,
    loadingTimeoutReplay: true,
    loadingTimeoutReplayTimes: MAX_RETRY,
  });

  // ---- 事件监听 ----
  jessibuca.on("play", () => {
    retryCount = 0;
  });

  jessibuca.on("timeUpdate", (ts: number) => {
    // ts 是当前帧的 pts（毫秒）
    lastPtsMs = ts;
  });

  jessibuca.on("kBps", (kbps: number) => {
    kbpsInfo.value = `${Math.round(kbps)}KB/s`;
  });

  jessibuca.on("videoInfo", (info: any) => {
    if (props.debug) {
      console.log("[Jessibuca] videoInfo:", info);
    }
  });

  jessibuca.on("error", (err: any) => {
    console.error("[Jessibuca] error:", err);
    const errCode = err?.code || "";
    const errMsg = String(err?.message || err || "");
    if (
      errCode === "mediaSourceH265NotSupport" ||
      errCode === "webcodecsH265NotSupport" ||
      errMsg.includes("H265") ||
      errMsg.includes("h265") ||
      errMsg.includes("HEVC")
    ) {
      errorMsg.value =
        "当前浏览器不支持 H.265/HEVC 编码播放，请使用支持 HEVC 硬解的浏览器";
      destroyPlayer();
    }
  });

  jessibuca.on("timeout", () => {
    if (retryCount < MAX_RETRY) {
      retryCount++;
      console.warn(`[Jessibuca] 超时，第 ${retryCount}/${MAX_RETRY} 次重试...`);
      // Jessibuca 内置重连，此处仅记录
    }
  });

  jessibuca.on("streamEnd", () => {
    if (props.debug) {
      console.log("[Jessibuca] streamEnd: 流结束");
    }
  });

  // 开始播放
  jessibuca.play(url);
}

async function destroyPlayer() {
  if (!jessibuca) return;
  isDestroying = true;
  try {
    await jessibuca.destroy();
  } catch (_e) {}
  jessibuca = null;

  // destroy 完成后再清理容器内残留 DOM，避免 removeChild 冲突
  const container = playerContainerRef.value;
  if (container) {
    container.innerHTML = "";
  }

  // 等待 WebGL/Worker 资源释放
  await new Promise<void>((r) => setTimeout(r, 200));
  isDestroying = false;
}

/** 时间轴 seek 跳转 */
function onTimelineSeek(sec: number) {
  commitWithTime(sec);
}

// ==================== Lifecycle ====================
onMounted(() => {
  const win = window as any;
  if (!win.Jessibuca) {
    errorMsg.value =
      "Jessibuca 播放器未加载，请检查 HTML 是否引入了 jessibuca.js";
    return;
  }
  playStream(currentUrl.value);
  startPlayTimer();
});

/** 定时同步播放进度到时间轴 */
function startPlayTimer() {
  playTimerId = setInterval(() => {
    if (!jessibuca || !timelineRef.value) return;

    // 使用 playStartTimeMs + lastPtsMs 计算当前播放时间
    // 对于回放流，pts 通常是从0开始的偏移量
    if (playStartTimeMs > 0 && lastPtsMs >= 0) {
      const timeMs = playStartTimeMs + lastPtsMs;
      timelineRef.value.updateRulerTime(timeMs);
      timelineRef.value.autoFollow();
      // 更新叠加时间
      overlayTimeText.value = `${fmtD(timeMs / 1000)} ${fmtTF(timeMs / 1000)}`;
    }
  }, 250);
}

onBeforeUnmount(() => {
  if (playTimerId) clearInterval(playTimerId);
  destroyPlayer();
});
</script>

<template>
  <div class="replay-page">
    <!-- 日期时间选择器 -->
    <div class="replay-toolbar">
      <span class="replay-toolbar__label">回放开始时间</span>
      <IDatePicker
        v-model="datetimeValue"
        type="datetime"
        value-format="YYYY-MM-DD HH:mm:ss"
        @change="onDatetimeChange"
      />
    </div>

    <!-- 播放器 -->
    <div class="player-wrap">
      <div
        v-show="!errorMsg"
        ref="playerContainerRef"
        class="jessibuca-container"
      />
      <div v-if="errorMsg" class="player-error">
        <div class="player-error__icon">⚠</div>
        <div class="player-error__text">{{ errorMsg }}</div>
      </div>
    </div>

    <!-- 刻度尺时间轴 -->
    <ITimelineRuler ref="timelineRef" @seek="onTimelineSeek" />
  </div>
</template>
